import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ARTIST } from "../lib/site";
import { TikTokIcon, InstagramIcon, YouTubeIcon } from "./BrandIcons";
import { trackClick } from "../lib/analytics";

const socials = [
  { id: "instagram", name: "Instagram", Icon: InstagramIcon, url: ARTIST.instagram },
  { id: "tiktok", name: "TikTok", Icon: TikTokIcon, url: ARTIST.tiktok },
  { id: "youtube", name: "YouTube", Icon: YouTubeIcon, url: ARTIST.youtube },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message envoyé ✦ Je reviens vers vous très vite.");
      setForm({ name: "", email: "", subject: "", message: "", website: "" });
    } catch (err) {
      toast.error("Envoi impossible. Vérifiez vos informations.");
    } finally {
      setLoading(false);
    }
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 lg:py-40 relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(212,175,55,0.06),transparent_55%)] pointer-events-none" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 relative">
        <div className="lg:col-span-5">
          <span className="text-xs sm:text-sm uppercase tracking-[0.3em] text-gold">Contact · Booking</span>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-tight text-white">
            Travaillons <span className="gold-text">ensemble</span>.
          </h2>
          <p className="text-base lg:text-lg font-light text-[#B8B8B8] leading-relaxed mt-6 max-w-md text-hover">
            Programmation, collaboration, presse ou simple message : écrivez-moi. Les professionnels de l'industrie
            sont les bienvenus.
          </p>

          <a
            href={`mailto:${ARTIST.email}`}
            data-testid="contact-email-link"
            className="inline-flex items-center gap-3 mt-8 text-white hover:text-gold transition-colors"
          >
            <Mail className="w-5 h-5 text-gold" /> {ARTIST.email}
          </a>

          <div className="flex items-center gap-3 mt-8">
            {socials.map((s) => (
              <a
                key={s.id}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackClick(s.id)}
                data-testid={`contact-social-${s.id}`}
                aria-label={s.name}
                className="w-12 h-12 border border-white/[0.12] flex items-center justify-center text-white/80 hover:text-gold hover:border-gold/40 transition-colors"
              >
                <s.Icon size={20} />
              </a>
            ))}
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7 backdrop-blur-2xl bg-white/[0.02] border border-white/[0.08] p-6 sm:p-8 space-y-5"
        >
          {/* Honeypot — hidden from humans, catches bots */}
          <input
            type="text"
            name="website"
            value={form.website}
            onChange={upd("website")}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            data-testid="contact-hp"
            className="absolute -left-[9999px] w-px h-px opacity-0 pointer-events-none"
          />
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#B8B8B8] block mb-2">Nom</label>
              <input
                data-testid="contact-name" required value={form.name} onChange={upd("name")}
                placeholder="Votre nom"
                className="w-full bg-black/40 border border-white/[0.1] px-4 py-3.5 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50 transition-colors"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#B8B8B8] block mb-2">Email</label>
              <input
                data-testid="contact-email" type="email" required value={form.email} onChange={upd("email")}
                placeholder="vous@email.com"
                className="w-full bg-black/40 border border-white/[0.1] px-4 py-3.5 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50 transition-colors"
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#B8B8B8] block mb-2">Objet</label>
            <input
              data-testid="contact-subject" value={form.subject} onChange={upd("subject")}
              placeholder="Booking, collaboration, presse…"
              className="w-full bg-black/40 border border-white/[0.1] px-4 py-3.5 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50 transition-colors"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#B8B8B8] block mb-2">Message</label>
            <textarea
              data-testid="contact-message" required rows={5} value={form.message} onChange={upd("message")}
              placeholder="Votre message…"
              className="w-full bg-black/40 border border-white/[0.1] px-4 py-3.5 text-sm text-white placeholder:text-[#8A8A8A] focus:outline-none focus:border-gold/50 transition-colors resize-none"
            />
          </div>
          <button
            data-testid="contact-submit" disabled={loading}
            className="inline-flex items-center gap-2 bg-gold text-black font-medium px-8 py-4 hover:bg-gold-light transition-colors disabled:opacity-50 text-sm tracking-wide"
          >
            <Send className="w-4 h-4" /> {loading ? "Envoi…" : "Envoyer le message"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
